import {
  findCatalogFamilyId,
  getMenuGroupItems,
  getMenuGroupTitle,
  getMenuSectionId,
  getMenuSectionName,
  resolveCatalogSelection,
} from "./familyMenu";

export const buildTopbarMenuStructure = (catalogMenu = [], navItems = []) => {
  const source = catalogMenu.length
    ? catalogMenu.filter((item) => item.activa !== false)
    : navItems.map((item) => ({
      name: item,
      groups: [],
    }));
  return source;
};

export const buildValidSelections = (collections = [], menuStructure = []) => [
  ...collections,
  ...menuStructure.map((item) => item.name),
  ...menuStructure.flatMap((item) => (item.groups || []).flatMap((group) => [
    getMenuGroupTitle(group),
    ...getMenuGroupItems(group).map((subItem) => subItem.name),
  ])),
];

const stripSuffix = (value = "") => String(value || "").trim();

export const slugifyRouteSegment = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " y ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const findMenuSectionBySlug = (menuStructure = [], slug = "") => {
  const normalizedSlug = stripSuffix(slug);
  return (Array.isArray(menuStructure) ? menuStructure : [])
    .find((item) => slugifyRouteSegment(getMenuSectionName(item)) === normalizedSlug) || null;
};

const findMenuGroupBySlug = (section, slug = "") => {
  const normalizedSlug = stripSuffix(slug);
  return (Array.isArray(section?.groups) ? section.groups : [])
    .find((group) => slugifyRouteSegment(getMenuGroupTitle(group)) === normalizedSlug) || null;
};

const findGroupItemBySlug = (group, slug = "") => {
  const normalizedSlug = stripSuffix(slug);
  return getMenuGroupItems(group).find((item) => slugifyRouteSegment(item.name) === normalizedSlug) || null;
};

const findSectionItemBySlug = (section, slug = "") =>
  (Array.isArray(section?.groups) ? section.groups : [])
    .flatMap((group) => getMenuGroupItems(group))
    .find((item) => slugifyRouteSegment(item.name) === stripSuffix(slug)) || null;

const buildCatalogSectionPath = (collection = "") => `/categoria/${slugifyRouteSegment(collection)}`;

const buildCatalogGroupPath = (collection = "", group = "") =>
  `${buildCatalogSectionPath(collection)}/${slugifyRouteSegment(group)}`;

const buildCatalogItemPath = (collection = "", subcollection = "", group = "") => {
  const itemSlug = slugifyRouteSegment(subcollection);
  if (group) {
    return `${buildCatalogGroupPath(collection, group)}/${itemSlug}`;
  }
  return `${buildCatalogSectionPath(collection)}/${itemSlug}`;
};

export const resolveCatalogRoute = ({
  menuStructure = [],
  sectionSlug = "",
  subcollectionSlug = "",
  familySlug = "",
  leafSlug = "",
} = {}) => {
  const resolvedSectionSlug = stripSuffix(sectionSlug);
  const resolvedSubcollectionSlug = stripSuffix(subcollectionSlug);
  const resolvedLeafSlug = stripSuffix(familySlug || leafSlug);
  const section = findMenuSectionBySlug(menuStructure, resolvedSectionSlug);

  if (!section) {
    return {
      collection: "",
      subcollection: "",
      familyId: 0,
    };
  }

  if (!resolvedSubcollectionSlug && !resolvedLeafSlug) {
    return {
      collection: getMenuSectionName(section),
      subcollection: "",
      familyId: getMenuSectionId(section),
    };
  }

  if (!resolvedLeafSlug) {
    const matchedGroup = findMenuGroupBySlug(section, resolvedSubcollectionSlug);
    if (matchedGroup) {
      return {
        collection: getMenuSectionName(section),
        subcollection: getMenuGroupTitle(matchedGroup),
        familyId: Number(matchedGroup?.id || 0),
      };
    }

    const directItem = findSectionItemBySlug(section, resolvedSubcollectionSlug);
    if (directItem?.name) {
      return {
        collection: getMenuSectionName(section),
        subcollection: directItem.name,
        familyId: Number(directItem.id || 0),
      };
    }

    return {
      collection: getMenuSectionName(section),
      subcollection: "",
      familyId: getMenuSectionId(section),
    };
  }

  const matchedGroup = findMenuGroupBySlug(section, resolvedSubcollectionSlug);
  if (matchedGroup) {
    const matchedItem = findGroupItemBySlug(matchedGroup, resolvedLeafSlug);
    if (matchedItem) {
      return {
        collection: getMenuSectionName(section),
        subcollection: matchedItem.name,
        familyId: Number(matchedItem.id || 0),
      };
    }
  }

  const fallbackItem = findSectionItemBySlug(section, resolvedLeafSlug);

  if (fallbackItem) {
    return {
      collection: getMenuSectionName(section),
      subcollection: fallbackItem.name,
      familyId: Number(fallbackItem.id || 0),
    };
  }

  return {
    collection: getMenuSectionName(section),
    subcollection: "",
    familyId: getMenuSectionId(section),
  };
};

export const buildCatalogPath = ({ collection = "", subcollection = "", familyId = 0, menuStructure = [] } = {}) => {
  if (!collection || collection === "Todos") {
    return "/catalogo";
  }

  const selection = resolveCatalogSelection(menuStructure, collection, subcollection);
  if (selection?.item?.name) {
    return buildCatalogItemPath(
      selection.section?.name || collection,
      selection.item.name,
      selection.group ? getMenuGroupTitle(selection.group) : ""
    );
  }

  if (selection?.group) {
    return buildCatalogGroupPath(selection.section?.name || collection, getMenuGroupTitle(selection.group));
  }

  if (selection?.section?.name) {
    return buildCatalogSectionPath(selection.section.name);
  }

  if (subcollection && Number(familyId) > 0) {
    return buildCatalogItemPath(collection, subcollection);
  }

  if (subcollection) {
    return buildCatalogGroupPath(collection, subcollection);
  }

  return buildCatalogSectionPath(collection);
};

export const buildProductDetailPath = (productName = "") =>
  `/${slugifyRouteSegment(productName)}`;


const getSelectedFamilyId = (selectedCollection, selectedSubcollection = "", menuStructure = []) => {
  if (!selectedCollection || selectedCollection === "Todos") {
    return 0;
  }

  const familyId = Number(findCatalogFamilyId(menuStructure, selectedCollection, selectedSubcollection));
  return Number.isFinite(familyId) && familyId > 0 ? familyId : 0;
};

export const buildCatalogSearchPath = ({ selectedCollection = "", selectedSubcollection = "", trimmedSearch = "", menuStructure = [] }) => {
  const selectedFamilyId = getSelectedFamilyId(selectedCollection, selectedSubcollection, menuStructure);
  const nextFamilyId = trimmedSearch ? 0 : selectedFamilyId;
  const nextCollection = trimmedSearch ? "" : selectedCollection;
  const nextSubcollection = trimmedSearch ? "" : selectedSubcollection;
  if (!trimmedSearch) {
    return {
      selectedFamilyId: nextFamilyId,
      url: buildCatalogPath({
        collection: nextCollection,
        subcollection: nextSubcollection,
        familyId: nextFamilyId,
        menuStructure,
      }),
    };
  }

  const params = new URLSearchParams();

  if (nextCollection && nextCollection !== "Todos") {
    params.set("collection", nextCollection);
  }
  if (nextSubcollection) {
    params.set("subcollection", nextSubcollection);
  }
  if (trimmedSearch) {
    params.set("search", trimmedSearch);
  }
  if (trimmedSearch || nextFamilyId > 0) {
    params.set("familia", String(nextFamilyId));
  }
  params.set("refresh", String(Date.now()));

  return {
    selectedFamilyId: nextFamilyId,
    url: `/catalogo${params.toString() ? `?${params.toString()}` : ""}`,
  };
};
